import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Search, Users, ClipboardList, ReceiptText, Wallet, X } from "lucide-react";

export const GlobalSearch = () => {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const { data, isFetching } = useQuery({
    queryKey: ["global-search", debounced],
    queryFn: async () => (await api.get("/search", { params: { q: debounced } })).data,
    enabled: debounced.length >= 2,
  });

  const go = (path) => { setOpen(false); setQ(""); navigate(path); };

  const groups = [
    { key: "employees", label: "Employees", icon: Users, rows: data?.employees || [], render: (r) => ({ title: r.name, sub: `${r.employee_id}${r.designation ? " · " + r.designation : ""}`, path: `/profile?emp=${r.employee_id}` }) },
    { key: "tasks", label: "Tasks", icon: ClipboardList, rows: data?.tasks || [], render: (r) => ({ title: r.project, sub: r.description, path: "/tasks" }) },
    { key: "invoices", label: "Invoices", icon: ReceiptText, rows: data?.invoices || [], render: (r) => ({ title: r.invoice_number, sub: r.consignee_name, path: "/invoices" }) },
    { key: "payslips", label: "Payslips", icon: Wallet, rows: data?.payslips || [], render: (r) => ({ title: r.employee_name, sub: r.month, path: "/payslips" }) },
  ];
  const total = groups.reduce((s, g) => s + g.rows.length, 0);

  return (
    <div ref={boxRef} className="relative hidden md:block w-64" data-testid="global-search">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <input
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder="Search employees, tasks, invoices…"
        data-testid="global-search-input"
        className="h-9 w-full rounded-lg border border-slate-200 bg-slate-50 pl-9 pr-8 text-sm text-navy outline-none focus:border-gold focus:bg-white"
      />
      {q && (
        <button onClick={() => { setQ(""); setOpen(false); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-navy" data-testid="global-search-clear">
          <X className="h-4 w-4" />
        </button>
      )}
      {open && debounced.length >= 2 && (
        <div className="absolute right-0 z-50 mt-2 w-80 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl" data-testid="global-search-results">
          {isFetching || q.trim() !== debounced ? (
            <div className="px-4 py-6 text-center text-sm text-slate-400">Searching…</div>
          ) : total === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-slate-400">No matches for “{debounced}”.</div>
          ) : (
            <div className="max-h-96 overflow-y-auto py-1">
              {groups.filter((g) => g.rows.length).map((g) => (
                <div key={g.key}>
                  <div className="flex items-center gap-2 px-4 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                    <g.icon className="h-3.5 w-3.5" /> {g.label}
                  </div>
                  {g.rows.map((r, i) => {
                    const it = g.render(r);
                    return (
                      <button key={i} onClick={() => go(it.path)} data-testid={`search-${g.key}-${i}`} className="flex w-full flex-col px-4 py-2 text-left hover:bg-slate-50">
                        <span className="truncate text-sm font-medium text-navy">{it.title}</span>
                        {it.sub && <span className="truncate text-xs text-slate-500">{it.sub}</span>}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
